import { CopyIcon } from "@xola/icons";
import { Badge, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import React, { ReactNode, useEffect } from "react";
import { useVisibility } from "@/hooks/useVisibility";
import { LocalizationProps } from "@/models/Locale";

interface CopyableFieldProps extends LocalizationProps {
    data: string;
    classNames?: { outerSpan?: string; badge?: string; tooltipContent?: string };
    dataTestId?: string;
    icon?: ReactNode;
    size?: "small" | "medium" | "large";
    showCopyIcon?: boolean;
    toolTipPlacement?: "bottom" | "top";
    children: ReactNode;
}

export const CopyableField = ({
    data,
    classNames,
    dataTestId,
    icon,
    size = "small",
    showCopyIcon = true,
    toolTipPlacement = "top",
    localize = false,
    children,
}: CopyableFieldProps) => {
    const [showCopiedPill, { showAndHideAfterDelay, hide }] = useVisibility(false);

    useEffect(() => hide(), [data, hide]);

    const handleCopyClick = (data: string) => {
        navigator.clipboard.writeText(data);
        showAndHideAfterDelay(2000);
    };

    return (
        <span
            className={clsx("hover:cursor-pointer hover:underline", classNames?.outerSpan ?? null)}
            data-testid={dataTestId}
            onClick={() => handleCopyClick(data)}
        >
            <Tooltip
                placement={toolTipPlacement}
                content={<span className={clsx(classNames?.tooltipContent)}>Click to copy</span>}
            >
                {icon}
                <span className="relative">
                    <span className={clsx({ "no-translate": !localize })}>{children}</span>

                    {showCopiedPill && (
                        <Badge
                            color="problem"
                            size={size}
                            className={clsx(
                                "absolute inset-y-0 inset-x-0 mx-auto bg-black text-white",
                                classNames?.badge ?? null,
                            )}
                        >
                            Copied
                        </Badge>
                    )}
                </span>
                {showCopyIcon && <CopyIcon className="ml-1" />}
            </Tooltip>
        </span>
    );
};
